import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { pdf } from '@react-pdf/renderer'
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import DetailsBox from '../../../components/Cards/DetailsBox'
import API from '../../../services/API'
import { UserContext } from '../../../App'
import PTSPerformancePDF from './PTSPerformancePDF'

const MetricTile = ({ label, value, target, met }) => (
  <Box flex={1} minWidth={140}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Stack direction="row" spacing={1} alignItems="baseline">
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 800 }}>
        {value}
      </Typography>
      {met !== null && met !== undefined && (
        <Chip
          size="small"
          label={met ? 'Target met' : 'Target missed'}
          color={met ? 'success' : 'error'}
          sx={{ borderRadius: 1 }}
        />
      )}
    </Stack>
    {target !== null && target !== undefined && (
      <Typography variant="caption" color="text.secondary">
        Target: {target}
      </Typography>
    )}
  </Box>
)

// On-time pickup/arrival %, aborted/no-show rates, and portal-booking share
// for this organisation, compared against the contract's SLA targets when
// set (§5.3). Reuses the same /pts/reports/journeys endpoint and on-time
// definitions as the tenant-wide PTS Reports page — no separate KPI logic.
const PTSPerformanceSection = ({ client }) => {
  const { user } = useContext(UserContext)
  const [start, setStart] = useState(
    moment().subtract(3, 'months').format('YYYY-MM-DD'),
  )
  const [end, setEnd] = useState(moment().format('YYYY-MM-DD'))
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [generating, setGenerating] = useState(false)

  const load = () => {
    if (!client?._id) return
    setLoading(true)
    setError('')
    API.get(
      `/pts/reports/journeys?start=${start}&end=${end}&organisation=${client._id}`,
    )
      .then((res) => {
        if (res.status === 200) setSummary(res.data.summary)
        else setError(res.data?.error || 'Unable to load performance data.')
      })
      .catch(() => setError('Unable to load performance data.'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client?._id])

  if (client?.orgType !== 'PTS') return null

  const sla = summary?.slaComparison || {}

  const downloadPDF = async () => {
    if (!summary) return
    setGenerating(true)
    try {
      const startLabel = moment(start).format('Do MMMM YYYY')
      const endLabel = moment(end).format('Do MMMM YYYY')
      const blob = await pdf(
        <PTSPerformancePDF
          orgName={client.orgName}
          rangeLabel={`${startLabel} – ${endLabel}`}
          summary={summary}
          printedBy={`Generated by ${user.firstName} ${user.lastName} on ${moment().format('Do MMMM YYYY, HH:mm')}`}
        />,
      ).toBlob()
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `PTS_Performance_${client.orgName?.replace(/\s+/g, '_') || 'Report'}_${moment().format('DDMMYYYY')}.pdf`
      link.click()
      URL.revokeObjectURL(url)
    } finally {
      setGenerating(false)
    }
  }

  return (
    <DetailsBox
      title="SLA & Performance"
      caption="On-time performance and volume, measured against the contract's SLA targets when set."
    >
      <Box>
        <Grid container spacing={2}>
          {error && (
            <Grid item xs={12}>
              <Alert severity="error">{error}</Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={1.5}
              alignItems={{ md: 'center' }}
            >
              <TextField
                type="date"
                label="From"
                size="small"
                value={start}
                onChange={(e) => setStart(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                type="date"
                label="To"
                size="small"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <Button
                variant="outlined"
                size="small"
                onClick={load}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Refresh'}
              </Button>
              <Box flex={1} />
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={downloadPDF}
                disabled={!summary || generating}
              >
                {generating ? 'Generating...' : 'Download PDF'}
              </Button>
            </Stack>
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" flexWrap="wrap" gap={3}>
              <MetricTile
                label="On-time pickup"
                value={
                  summary?.pickupKPI?.pct !== null &&
                  summary?.pickupKPI?.pct !== undefined
                    ? `${summary.pickupKPI.pct}%`
                    : '—'
                }
                target={
                  sla.onTimePickup ? `≥ ${sla.onTimePickup.target}%` : null
                }
                met={sla.onTimePickup?.met}
              />
              <MetricTile
                label="On-time arrival"
                value={
                  summary?.arrivalKPI?.pct !== null &&
                  summary?.arrivalKPI?.pct !== undefined
                    ? `${summary.arrivalKPI.pct}%`
                    : '—'
                }
                target={
                  sla.onTimeArrival ? `≥ ${sla.onTimeArrival.target}%` : null
                }
                met={sla.onTimeArrival?.met}
              />
              <MetricTile
                label="Aborted"
                value={`${summary?.aborted ?? 0}`}
                target={sla.aborted ? `≤ ${sla.aborted.target}%` : null}
                met={sla.aborted?.met}
              />
              <MetricTile
                label="No-show"
                value={`${summary?.noShow ?? 0}`}
                target={sla.noShow ? `≤ ${sla.noShow.target}%` : null}
                met={sla.noShow?.met}
              />
              <MetricTile
                label="Portal-booking share"
                value={
                  summary?.portalBookingSharePct !== null &&
                  summary?.portalBookingSharePct !== undefined
                    ? `${summary.portalBookingSharePct}%`
                    : '—'
                }
              />
              <MetricTile
                label="Total journeys"
                value={`${summary?.totalJourneys ?? 0}`}
              />
            </Stack>
          </Grid>
        </Grid>
      </Box>
    </DetailsBox>
  )
}

export default PTSPerformanceSection
